import { Dispatch, SetStateAction, useCallback } from "react"
import { makeStyles } from "tss-react/mui"
import { Stack, Tooltip } from "@mui/material"
import { faGrid, faGrid2, faHeart } from "@fortawesome/pro-solid-svg-icons"
import { faHeart as faHeartBorder } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { selectUserType } from "../../../../../../modules/login/loginSelectors"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"

import { ProductWish } from "../productWish/ProductWish"
import { FilterSearch } from "../../FilterSearch"
import { ShopFiltersTypes } from "../ShopList"
import { ActionButton } from "./ActionButton"

const useStyles = makeStyles()((theme) => ({
    iconLikeActive: {
        color: theme.palette.tailwind.blue[500],
        transition: "all 0.3s ease-out",
        "&:hover": {
            color: theme.palette.tailwind.blue[600],
        },
    },
}))

type ActionButtonsProps = Readonly<{
    filters: ShopFiltersTypes
    setFilters: Dispatch<SetStateAction<ShopFiltersTypes>>
    isProductWish: boolean
}>

export function ActionButtons({ filters, setFilters, isProductWish }: ActionButtonsProps) {
    const { classes } = useStyles()
    const userType = useAppSelector(selectUserType)
    const isOnBehalfOf = userType !== UserType.USER

    const handleSearch = useCallback(
        (searchText: string) => {
            setFilters((prev) => {
                if (prev.search === searchText) return prev
                return { ...prev, search: searchText }
            })
        },
        [setFilters],
    )
    const handleFavoriteClick = () => {
        setFilters((prev) => {
            return { ...prev, isFavorite: !prev.isFavorite }
        })
    }
    const handleChangeViewClick = () => {
        setFilters((prev) => {
            return { ...prev, isListView: !prev.isListView }
        })
    }

    return (
        <Stack flexDirection="row" alignItems="center" flex="none" gap=".75rem">
            <FilterSearch search={filters.search} setSearch={handleSearch} />
            {!!isProductWish && <ProductWish />}

            {!isOnBehalfOf && (
                <LocalizedStrict
                    id={filters.isFavorite ? "create-order-favorite-show-all-products" : "create-order-favorite"}
                    attrs={{ title: true }}
                >
                    <Tooltip title="View my favorite products / Show all products">
                        <div>
                            <ActionButton
                                icon={filters.isFavorite ? faHeart : faHeartBorder}
                                handleButtonClick={handleFavoriteClick}
                                updateClassName={filters.isFavorite ? classes.iconLikeActive : ""}
                            />
                        </div>
                    </Tooltip>
                </LocalizedStrict>
            )}
            <LocalizedStrict
                id={filters.isListView ? "create-order-grid" : "create-order-list"}
                attrs={{ title: true }}
            >
                <Tooltip title={filters.isListView ? "Show a grid of products" : "Show a list of products"}>
                    <div>
                        <ActionButton
                            icon={filters.isListView ? faGrid2 : faGrid}
                            handleButtonClick={handleChangeViewClick}
                        />
                    </div>
                </Tooltip>
            </LocalizedStrict>
        </Stack>
    )
}
